import { forwardRef, useMemo } from "react";

import Box, { BoxProps } from "@mui/material/Box";
import { SxProps, Theme } from "@mui/material/styles";
import { useTheme } from "@mui/styles";

import { useInteractiveElementEvents } from "../../../hooks/useInteractiveElementEvents";
import { LegacyZeffyCorePalette, IconSize } from "../../../types/mui";
import { getNestedValueFromPalette, ZeffyColorTokenTextVibes } from "../helpers";
import { getIconSize } from "../helpers/iconUtils";

import { mergeSx } from "@/helpers/mergeSx";
import { IconElement } from "@/icons/types";

export type DashboardIconProps = Omit<BoxProps, "classes" | "children"> & {
  vibe?: LegacyZeffyCorePalette | ZeffyColorTokenTextVibes | "inherit" | "currentColor";
  size?: IconSize;
  children: IconElement;
  sx?: SxProps<Theme>;
  inline?: boolean;
};

export const DashboardIcon = forwardRef<HTMLDivElement, DashboardIconProps>(
  ({ vibe = "tertiary", size = "medium", inline = false, children, onKeyDown, onKeyUp, onClick, sx, ...rest }, ref) => {
    const theme = useTheme();
    const { handleKeyDown, handleKeyUp } = useInteractiveElementEvents<HTMLDivElement>({ onClick, onKeyDown, onKeyUp });

    const iconSize = getIconSize(theme, size, inline);

    const vibeColor = useMemo(() => {
      if (vibe === "inherit" || vibe === "currentColor") {
        return undefined;
      }

      if (vibe in theme.palette && "main" in theme.palette[vibe as LegacyZeffyCorePalette]) {
        // Legacy token detected
        return theme.palette[vibe as LegacyZeffyCorePalette].main;
      }

      const textColor = getNestedValueFromPalette(theme.palette, vibe);

      if (!textColor) {
        throw new Error(`Invalid vibe: ${vibe}`);
      }

      return textColor;
    }, [vibe, theme]);

    const styles = mergeSx(
      {
        fill: vibe === "inherit" ? "inherit" : "currentColor",
        color: vibeColor,
        width: iconSize,
        height: iconSize,
        lineHeight: `${iconSize}px`,
        fontSize: iconSize,
        ["& > svg"]: {
          width: "100%",
          height: "100%",
        },
      },
      sx
    );

    return (
      <Box ref={ref} sx={styles} {...rest} onKeyDown={handleKeyDown} onKeyUp={handleKeyUp} onClick={onClick}>
        {children}
      </Box>
    );
  }
);

DashboardIcon.displayName = "DashboardIcon";
